/**
 * Списание и корректировка с телефона: брак, просрочка, недостача при
 * пересчёте. Goods are found by scan or name; the document is posted at once
 * and every register picks up the new stock on its next sync.
 */
import type { ProductDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import { UNITS, formatQuantity, formatQuantityValue, isValidQuantity, parseQuantity } from '@pos/business-logic';
import { Button, EmptyState, Field, Input, ListRow, Notice, SearchField, Segmented, cn, toast } from '@pos/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Minus, PackageMinus, Plus, ScanLine, Trash2 } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../app/auth';
import { BranchChip } from '../../app/BranchChip';
import { Screen } from '../../app/Screen';
import { lookupCode, useBranch, useBranches, useProducts } from '../../app/data';
import { ScannerSheet } from '../../lib/Scanner';

type Kind = 'WRITE_OFF' | 'ADJUSTMENT';

const REASONS: Record<Kind, string[]> = {
  WRITE_OFF: ['Брак', 'Просрочка', 'Порча', 'Бой', 'Для нужд магазина'],
  ADJUSTMENT: ['Пересчёт', 'Пересорт', 'Ошибка прихода', 'Найдено'],
};

interface Line {
  key: string;
  product: ProductDto;
  variantId: string | null;
  name: string;
  stock: number;
  /** Raw text; for a correction it may start with a minus. */
  quantity: string;
}

/** Thousandths, signed for a correction; null while the text is not a quantity. */
function lineQty(l: Line, kind: Kind): number | null {
  const text = l.quantity.trim();
  const negative = kind === 'ADJUSTMENT' && text.startsWith('-');
  const q = parseQuantity(negative ? text.slice(1) : text);
  if (q === null || !isValidQuantity(q, l.product.unit)) return null;
  return negative ? -q : q;
}

export function WriteOffPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const selected = useBranch((s) => s.branchId);
  const branches = useBranches().data ?? [];
  // «Все филиалы» is a view, not a place goods can leave from.
  const branchId = selected ?? (branches.length === 1 ? (branches[0]?.id ?? null) : null);
  const [kind, setKind] = useState<Kind>('WRITE_OFF');
  const [reason, setReason] = useState(REASONS.WRITE_OFF[0] ?? '');
  const [lines, setLines] = useState<Line[]>([]);
  const [scan, setScan] = useState(false);
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim());
  const found = useProducts(query, query.length >= 2);
  const [done, setDone] = useState<string | null>(null);

  const add = (product: ProductDto, variantId: string | null, qty = 1000) => {
    setSearch('');
    const variant = product.variants.find((v) => v.id === variantId);
    setLines((ls) => {
      const existing = ls.find((l) => l.product.id === product.id && l.variantId === variantId);
      if (existing) {
        const next = Math.abs(lineQty(existing, kind) ?? 0) + qty;
        return ls.map((l) => (l === existing ? { ...l, quantity: formatQuantityValue(next) } : l));
      }
      return [
        {
          key: crypto.randomUUID(),
          product,
          variantId,
          name: variant ? `${product.name} · ${variant.name}` : product.name,
          stock: variant?.stock ?? product.stock,
          quantity: formatQuantityValue(qty),
        },
        ...ls,
      ];
    });
    navigator.vibrate?.(20);
  };

  const onCode = async (code: string) => {
    setScan(false);
    const hit = await lookupCode(code);
    if (!hit) {
      toast.error('Товар не найден', { description: `Штрихкод ${code}` });
      return;
    }
    add(hit.product, hit.variantId, hit.quantity);
  };

  const update = (key: string, quantity: string) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, quantity } : l)));
  const step = (l: Line, d: number) => {
    const unit = l.product.unit === 'PIECE' ? 1000 : 100;
    const cur = lineQty(l, kind) ?? 0;
    const next = cur + d * unit;
    if (kind === 'WRITE_OFF') update(l.key, formatQuantityValue(Math.max(unit, next)));
    else update(l.key, next === 0 ? formatQuantityValue(d * unit) : `${next < 0 ? '-' : ''}${formatQuantityValue(Math.abs(next))}`);
  };

  const invalid = lines.length === 0 || lines.some((l) => lineQty(l, kind) === null) || !reason.trim() || !branchId;

  const post = useMutation({
    mutationFn: () =>
      api.post<{ id: string; number: string }>('/stock/documents', {
        type: kind,
        branchId,
        reason: reason.trim(),
        items: lines.map((l) => ({ productId: l.product.id, variantId: l.variantId, quantity: lineQty(l, kind) ?? 0 })),
      }),
    onSuccess: async (r) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['stock'] }),
        qc.invalidateQueries({ queryKey: ['stock-summary'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['products'] }),
      ]);
      setDone(r.number);
    },
    onError: (e) => toast.error('Документ не проведён', { description: e instanceof ApiError ? e.message : 'Проверьте связь и повторите' }),
  });

  const title = kind === 'WRITE_OFF' ? 'Списание' : 'Корректировка';

  if (done) {
    return (
      <Screen title={title} back="/stock">
        <div className="flex flex-col items-center px-4 pt-12 text-center">
          <div className="flex size-20 items-center justify-center rounded-full bg-success-soft text-success animate-pop-in">
            <Check className="size-10" strokeWidth={2.5} />
          </div>
          <h2 className="mt-6 text-2xl font-bold">
            {title} {done} проведено
          </h2>
          <p className="mt-2 text-md text-ink-3">Остатки обновлены; кассы получат их при синхронизации.</p>
          <div className="mt-10 flex w-full flex-col gap-2">
            <Button
              size="xl"
              variant="primary"
              onClick={() => {
                setDone(null);
                setLines([]);
              }}
            >
              Ещё документ
            </Button>
            <Button size="xl" onClick={() => navigate('/stock')}>
              К складу
            </Button>
          </div>
        </div>
      </Screen>
    );
  }

  return (
    <Screen title={title} back="/stock">
      <div className="space-y-4 pb-28">
        <Segmented
          label="Документ"
          value={kind}
          onChange={(k) => {
            setKind(k);
            setReason(REASONS[k][0] ?? '');
            // A correction's sign is part of the number; a write-off never has one.
            if (k === 'WRITE_OFF') setLines((ls) => ls.map((l) => ({ ...l, quantity: l.quantity.replace(/^-/, '') })));
          }}
          options={[
            { value: 'WRITE_OFF', label: 'Списание' },
            { value: 'ADJUSTMENT', label: 'Корректировка' },
          ]}
          className="w-full [&>button]:flex-1"
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-ink-3">{kind === 'WRITE_OFF' ? 'Товар уходит со склада' : 'Плюс — нашли, минус — не хватает'}</p>
          <BranchChip />
        </div>
        {!branchId && <Notice tone="warning">Выберите филиал — со «всех филиалов» списать нельзя.</Notice>}

        <div>
          <p className="mb-2 text-sm font-medium text-ink-2">Причина</p>
          <div className="flex flex-wrap gap-2">
            {REASONS[kind].map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={reason === r}
                onClick={() => setReason(r)}
                className={cn(
                  'h-10 rounded-full border px-4 text-base',
                  reason === r ? 'border-ink bg-ink font-medium text-white' : 'border-line-strong bg-surface active:bg-sunken',
                )}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <Button size="xl" variant="primary" className="w-full" icon={<ScanLine className="size-5" />} onClick={() => setScan(true)}>
          Сканировать товар
        </Button>
        <div>
          <SearchField size="lg" value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} placeholder="Или найдите по названию" aria-label="Найти товар" />
          {query.length >= 2 && (
            <div className="mt-2 overflow-hidden rounded-2xl border border-line bg-surface">
              {(found.data?.items ?? []).slice(0, 8).flatMap((p) =>
                (p.hasVariants && p.variants.length ? p.variants.map((v) => ({ p, v })) : [{ p, v: null }]).map(({ p: prod, v }) => (
                  <ListRow
                    key={`${prod.id}:${v?.id ?? ''}`}
                    title={v ? `${prod.name} · ${v.name}` : prod.name}
                    subtitle={`Остаток ${formatQuantity(v?.stock ?? prod.stock, prod.unit)}`}
                    trailing={<Plus className="size-5 text-primary" />}
                    onClick={() => add(prod, v?.id ?? null)}
                  />
                )),
              )}
              {found.data && found.data.items.length === 0 && <ListRow title={<span className="text-ink-3">Ничего не найдено</span>} />}
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line-strong bg-surface">
            <EmptyState icon={<PackageMinus />} title="Добавьте товары" description="Сканируйте штрихкод или найдите товар по названию." />
          </div>
        ) : (
          <ul className="space-y-3">
            {lines.map((l) => {
              const q = lineQty(l, kind);
              const unitLabel = UNITS[l.product.unit].label;
              const over = kind === 'WRITE_OFF' && q !== null && q > l.stock;
              return (
                <li key={l.key} className="rounded-2xl border border-line bg-surface p-4">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-md font-medium">{l.name}</p>
                      <p className="text-sm text-ink-3">Остаток {formatQuantity(l.stock, l.product.unit)}</p>
                    </div>
                    <button type="button" aria-label={`Убрать ${l.name}`} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="-m-2 flex size-11 items-center justify-center rounded-full text-ink-3 active:bg-rail">
                      <Trash2 className="size-5" />
                    </button>
                  </div>
                  <Field
                    label={kind === 'WRITE_OFF' ? `Списать, ${unitLabel}` : `Изменить на, ${unitLabel}`}
                    error={q === null ? 'Проверьте количество' : undefined}
                    hint={over ? 'Больше, чем на остатке — остаток станет отрицательным' : undefined}
                    className="mt-3"
                  >
                    {(a) => (
                      <div className="flex items-center gap-1">
                        <button type="button" aria-label="Меньше" onClick={() => step(l, -1)} className="flex size-12 shrink-0 items-center justify-center rounded-md border border-line-strong active:bg-sunken">
                          <Minus className="size-5" />
                        </button>
                        <Input {...a} size="lg" inputMode="decimal" className="min-w-0 text-center tnum" value={l.quantity} onChange={(e) => update(l.key, e.target.value)} />
                        <button type="button" aria-label="Больше" onClick={() => step(l, 1)} className="flex size-12 shrink-0 items-center justify-center rounded-md border border-line-strong active:bg-sunken">
                          <Plus className="size-5" />
                        </button>
                      </div>
                    )}
                  </Field>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {lines.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-md">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <p className="min-w-0 flex-1 text-base text-ink-2">
              {lines.length} поз. · {reason}
            </p>
            <Button size="xl" variant={kind === 'WRITE_OFF' ? 'danger' : 'primary'} disabled={invalid} loading={post.isPending} onClick={() => post.mutate()}>
              Провести
            </Button>
          </div>
        </div>
      )}

      <ScannerSheet open={scan} onOpenChange={setScan} onCode={(c) => void onCode(c)} title="Сканировать товар" />
    </Screen>
  );
}
